import { useState, useRef, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import { ShoppingBag, ChevronRight, Sparkles, Tag, Package, ShoppingCart, PackageSearch } from "lucide-react";
import { money, storeImg } from "../lib/store";

// Store nav entry for the left sidebar with a premium desktop hover mega-menu flyout.
export const StoreNavItem = ({ collapsed, onNavigate, data }) => {
  const [open, setOpen] = useState(false);
  const [top, setTop] = useState(72);
  const closeT = useRef(null);
  const rootRef = useRef(null);

  const enter = () => {
    clearTimeout(closeT.current);
    if (window.innerWidth >= 1024) {
      const r = rootRef.current?.getBoundingClientRect();
      if (r) setTop(Math.max(16, Math.min(r.top, window.innerHeight - 470)));
      setOpen(true);
    }
  };
  const leave = () => { closeT.current = setTimeout(() => setOpen(false), 320); };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const mm = data || {};
  const left = collapsed ? 72 : 248;

  return (
    <div ref={rootRef} className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <NavLink
        to="/store"
        onClick={onNavigate}
        data-testid="nav-link-store"
        className={({ isActive }) => `nav-item group flex items-center gap-3 h-10 ${collapsed ? "justify-center px-0" : "px-3"} font-stencil font-bold uppercase text-[13px] tracking-wide ${isActive ? "active text-[#D4A94A] bg-[#D4A94A]/10" : "text-[#94A3B8] hover:text-[#F1F5F9] hover:bg-[#13151B]"}`}
      >
        <ShoppingBag className="w-[18px] h-[18px] shrink-0" strokeWidth={2.1} />
        {!collapsed && <span className="truncate">Store</span>}
        {!collapsed && <ChevronRight className="w-3.5 h-3.5 ml-auto opacity-50 group-hover:opacity-100 transition-opacity" />}
        {collapsed && <span className="nav-tip glass px-2.5 py-1.5 text-xs font-stencil font-bold uppercase text-[#F1F5F9]">Store</span>}
      </NavLink>

      {open && (
        <div
          data-testid="store-mega-menu"
          onMouseEnter={enter}
          onMouseLeave={leave}
          className="hidden lg:block fixed z-[60] w-[640px] bg-[#0E0F12] border border-[#D4A94A]/30 tactical-frame shadow-2xl shadow-black/60 animate-in fade-in slide-in-from-left-2 duration-150"
          style={{ left, top }}
        >
          {/* invisible hover bridge so the pointer can cross the gap to the menu */}
          <div className="absolute top-0 -left-3 w-3 h-full" />
          <div className="grid grid-cols-5">
            {/* Featured */}
            <div className="col-span-2 relative p-5 bg-[#0B0C0E] border-r border-[#2A2E3D] overflow-hidden">
              {storeImg({ src: mm.image_url }) && <img src={storeImg({ src: mm.image_url })} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />}
              <div className="relative">
                <div className="overline text-[#D4A94A] flex items-center gap-1 mb-2"><Sparkles className="w-3 h-3" /> Featured</div>
                <div className="font-heading text-2xl font-black uppercase leading-none">{mm.headline || "REP THE DOGS"}</div>
                <p className="font-mono text-[11px] text-[#94A3B8] mt-2 leading-relaxed">{mm.description}</p>
                <Link to="/store" onClick={onNavigate} className="inline-flex items-center gap-1 mt-4 px-3 py-2 bg-[#D4A94A] hover:bg-[#E5BD60] text-black font-stencil font-black uppercase text-[11px] tactical-frame-sm">Shop the collection <ChevronRight className="w-3.5 h-3.5" /></Link>
              </div>
            </div>
            {/* Categories + links */}
            <div className="col-span-3 p-5">
              <div className="overline mb-2">Shop by category</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-4">
                <Link to="/store" onClick={onNavigate} className="font-stencil font-bold uppercase text-xs text-[#CBD5E1] hover:text-[#D4A94A] py-1">All Merchandise</Link>
                {(mm.categories || []).slice(0, 7).map((c) => (
                  <Link key={c.id} to={`/store?cat=${c.slug}`} onClick={onNavigate} className="font-stencil font-bold uppercase text-xs text-[#CBD5E1] hover:text-[#D4A94A] py-1 truncate">{c.name}</Link>
                ))}
              </div>
              {(mm.featured_products || []).length > 0 && (
                <>
                  <div className="overline mb-2">Featured products</div>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {mm.featured_products.slice(0, 3).map((p) => (
                      <Link key={p.id} to={`/store/product/${p.slug}`} onClick={onNavigate} className="group block">
                        <div className="aspect-square bg-[#0B0C0E] border border-[#2A2E3D] group-hover:border-[#D4A94A] overflow-hidden tactical-frame-sm transition-colors">
                          {storeImg(p.images?.[0]) && <img src={storeImg(p.images[0])} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="font-mono text-[10px] text-[#CBD5E1] truncate mt-1">{p.name}</div>
                        <div className="font-heading font-black text-[11px] text-[#D4A94A]">{money(p.effective_cents, p.currency)}</div>
                      </Link>
                    ))}
                  </div>
                </>
              )}
              <div className="flex items-center gap-3 flex-wrap border-t border-[#2A2E3D] pt-3 font-stencil font-bold uppercase text-[10px]">
                <Link to="/store" onClick={onNavigate} className="text-[#D4A94A] hover:text-[#E5BD60]">View all products</Link>
                {mm.has_new_arrivals && <Link to="/store" onClick={onNavigate} className="text-[#94A3B8] hover:text-[#D4A94A]">New Arrivals</Link>}
                {mm.has_sale && <Link to="/store" onClick={onNavigate} className="text-[#E4443A] hover:opacity-80 inline-flex items-center gap-1"><Tag className="w-3 h-3" /> Sale</Link>}
                <span className="flex-1" />
                <Link to="/store/orders" onClick={onNavigate} className="text-[#94A3B8] hover:text-[#D4A94A] inline-flex items-center gap-1"><Package className="w-3 h-3" /> Orders</Link>
                <Link to="/store/track" onClick={onNavigate} data-testid="megamenu-track-order" className="text-[#94A3B8] hover:text-[#D4A94A] inline-flex items-center gap-1"><PackageSearch className="w-3 h-3" /> Track</Link>
                <Link to="/store/cart" onClick={onNavigate} className="text-[#94A3B8] hover:text-[#D4A94A] inline-flex items-center gap-1"><ShoppingCart className="w-3 h-3" /> Cart</Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
